/**
 * URLs for the Beacon Ops "org" Filament panel (a separate Laravel app / host).
 *
 * Two shapes matter:
 *  - The **host origin** (for host-level Laravel routes like the handoff
 *    exchange at /auth/handoff-exchange).
 *  - The **panel URL**. Locally the ops + org panels share one host via path
 *    prefixes (org panel at /org-panel); in prod the org panel is served at its
 *    own domain root (org.davidsbeacon.com/…, no /org-panel prefix).
 *
 * Resolution of the host origin:
 *  1. VITE_BEACON_ORG_URL if set (per-environment override / staging)
 *  2. localhost → http://localhost:8000
 *  3. production default → https://org.davidsbeacon.com
 *
 * Note: do NOT hardcode a localhost value in .env.local — Vite loads .env.local
 * in production builds too, so it would leak into the deployed bundle. The
 * hostname fallback below already resolves local vs prod correctly with no env.
 */
export function orgHostBaseUrl(): string {
  const fromEnv = import.meta.env.VITE_BEACON_ORG_URL as string | undefined
  if (fromEnv && fromEnv.trim() !== '') {
    return fromEnv.replace(/\/$/, '')
  }
  if (
    typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
  ) {
    return 'http://localhost:8000'
  }
  return 'https://org.davidsbeacon.com'
}

/** True when the host is a localhost origin (path-mode panels). */
function isLocalHost(host: string): boolean {
  return /^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?$/.test(host)
}

/**
 * Full URL into the org Filament panel.
 * Local (path mode): {host}/org-panel{path}. Prod (domain mode): {host}{path}.
 * Pass a sub-path like '/appointments'; default is the panel root.
 */
export function orgPanelUrl(path = ''): string {
  const host = orgHostBaseUrl()
  const prefix = isLocalHost(host) ? '/org-panel' : ''
  const clean = path && !path.startsWith('/') ? `/${path}` : path
  return `${host}${prefix}${clean}`
}
